import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import Tree, { ItemId } from 'tree-dnd'

import * as HandbookItemTypes from 'components/Handbook2_0/Sidebar/HandbookItemTypes'
import { Icon } from 'components/Handbook2_0/Sidebar/HandbookItemTypes/Components/Icon'
import { NavItem } from 'components/NavItem'
import { HandbookItemType } from 'types'

import { useHandbookItems } from 'components/Handbook2_0/Sidebar/HandbookItemProvider'
import Styles from './HandbookItems.module.scss'
import { useOffset } from 'components/Handbook2_0/Sidebar/hooks/useOffset'
import { useRootStore } from 'stores/RootStore'
import { LocationPickerFolder, SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { ExpandButton } from 'components/NavItem/ExpandButton/ExpandButton'
import { Check } from 'blaze-ui/icons/16'

interface HandbookItemsProps {
  onSelect: (folder: SelectedFolder | null) => void
  depthOffset?: 0 | 1
  activeFolder: LocationPickerFolder | null | undefined
}

export const HandbookItems = observer(function HandbookItems({
  onSelect,
  depthOffset = 0,
  activeFolder,
}: HandbookItemsProps) {
  const offset = useOffset()
  const { currentHandbook: handbook, treeStore } = useHandbookItems()
  const handbooks = useRootStore().handbooks

  if (!treeStore?.tree || !handbook) return null

  return (
    <div>
      <Tree
        tree={treeStore.tree}
        renderItem={(props) => {
          const isHandbookFolder = props.item.data.nested_handbook && props.item.data.handbook_mode
          const isExpandable = !(
            (!props.item.data.nested_handbook && (!props.item.children || props.item.children.length === 0)) ||
            !isHandbookFolder
          )
          const isDisabled = props.item.data.type !== 'nested_handbook'
          const isActive = !!(!isDisabled && activeFolder?.id && props.item.data.folder.key === activeFolder.id)

          return (
            <div ref={props.provided.innerRef} className={cx({ [Styles.disabled]: isDisabled })}>
              {props.item.data.type === HandbookItemType.Section ? (
                <HandbookItemTypes.Section item={props.item.data} depth={props.depth + depthOffset} />
              ) : (
                <NavItem.Root
                  onPress={() => {
                    if (isDisabled) return

                    if (props.item.data.type === HandbookItemType.NestedHandbook) {
                      const data = props.item.data
                      onSelect({
                        id: data.folder.key,
                        title: data.title,
                        seo_string: data.folder.almanac_file.seo_string,
                        handbook_key: data.nested_handbook.key,
                        default_view: data.folder.default_view,
                        database_mode: data.database_mode,
                        header_emoji: data.folder.header_emoji,
                      })
                    }
                  }}
                  indentLevel={props.depth + depthOffset}
                  isActive={isActive}
                  elementType="div"
                >
                  <NavItem.Emoji>
                    {props.item.data.favicon ? (
                      <img
                        className={cx(Styles.icon, Styles.link)}
                        src={props.item.data.favicon}
                        alt={props.item.data.title}
                      />
                    ) : (
                      <Icon item={props.item.data} isExpanded={props.item.isExpanded} />
                    )}
                  </NavItem.Emoji>
                  <NavItem.Label label={props.item.data.title} />
                  {isExpandable && (
                    <ExpandButton
                      isExpanded={props.item.isExpanded}
                      onPress={() => {
                        if (props.item.isExpanded) {
                          props.onCollapse(props.item.id)
                        } else {
                          props.onExpand(props.item.id)
                        }
                      }}
                    />
                  )}
                  {isActive && (
                    <div className={Styles.checkIcon}>
                      <Check />
                    </div>
                  )}
                </NavItem.Root>
              )}
            </div>
          )
        }}
        onExpand={(itemId: ItemId) => handbooks.items.get(itemId.toString())?.expand()}
        onCollapse={(itemId: ItemId) => handbooks.items.get(itemId.toString())?.collapse()}
        offsetPerLevel={offset}
        isDragEnabled={false}
        isNestingEnabled
        renderDropPlaceholder={() => null}
      />
    </div>
  )
})
